import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DbContext, DbTable } from '../data/dbcontext.service';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord
} from '../../models/projects/operation.models';
import { LocalOperationSaveService } from './operation/local-operation-save.service';
import { DailyLogService } from '../daily-log/daily-log.service';
import { AppDataSummary } from '../../models/app-data/app-data';
import { SetupService } from '../setup/setup.service';
import { OperationService } from '../operations/operation/operation.service';
import { SetupChangeLogRecord } from '../../models/setup/setup.models';
import { WeatherReading } from '../../models/weather-readings/weather-readings.models';
import { WeatherReadingsService } from '../weather-readings/weather-readings.service';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class LocalSaveService {
  config = AppConfig.settings;
  private readonly operationsDataTable: DbTable;
  private readonly stepCollectionsDataTable: DbTable;
  private readonly dailyLogTable: DbTable;
  private readonly weatherReadingTable: DbTable;
  private readonly psoTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private localOperationSaveService: LocalOperationSaveService,
    private dailyLogService: DailyLogService,
    private weatherReadingService: WeatherReadingsService,
    private setupService: SetupService,
    private operationService: OperationService,
  ) {
    this.operationsDataTable = this.dbContext.tables.find(t => t.name === 'OperationsData');
    this.stepCollectionsDataTable = this.dbContext.tables.find(t => t.name === 'StepCollectionsData');
    this.dailyLogTable = this.dbContext.tables.find(t => t.name === 'DailyLogData');
    this.psoTable = this.dbContext.tables.find(t => t.name === 'ProjectSpecificOperationInstance');
    this.weatherReadingTable = this.dbContext.tables.find(t => t.name === 'WeatherLogData');
  }

  saveLocalOperation(id: number, operationInstance: DailyLogOperationInstance) {
    this.localOperationSaveService.saveProjectSpecificOperationInstance(id, operationInstance);
  }

  saveLocalOperations(operations: DailyLogOperationInstance[]) {
    operations.forEach(operation => {
      this.localOperationSaveService
        .saveProjectSpecificOperationInstance(operation.id ? operation.id : operation.localId, operation);
    });
  }

  async saveDailyLogRecords(records: DailyOperationLogRecord[]) {
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      await this.dailyLogService.saveDailyLogRecord(record.id ? record.id : record.localId, record);
    }
  }

  async saveWeatherReadings(weatherReadings: WeatherReading[]) {
    for (let i = 0; i < weatherReadings.length; i++) {
      if (!weatherReadings[i].id && !weatherReadings[i].localId) {
        weatherReadings[i].localId = await this.weatherReadingService.getNewWeatherReadingLocalId();
      }

      const weatherReading = weatherReadings[i];
      await this.dbContext.addOrUpdate(this.weatherReadingTable, weatherReading.id ? weatherReading.id : weatherReading.localId, weatherReading);
    }
  }

  async saveSetupChangeRecords(records: SetupChangeLogRecord[]) {
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      await this.setupService.saveSetupChangeLogRecord(record.id ? record.id : record.localId, record);
    }
  }

  removeProjectSpecificOperation(id: number) {
    if (id) {
      this.dbContext.remove(this.psoTable, id);
    }
  }

  removeDailyLogRecords(records: DailyOperationLogRecord[]) {
    records.forEach(record => {
      this.dbContext.remove(this.dailyLogTable, record.id || record.localId);
    });
  }

  deleteLocalAppData(appData: AppDataSummary) {
    appData.dailyLogRecordIds.forEach(id => {
      this.dailyLogService.deleteLocalDailyLogRecord(id);
    });

    appData.setupChangeLogRecordIds.forEach(id => {
      this.setupService.deleteLocalSetupChangeLogRecord(id);
    });

    appData.dailyLogOperationInstanceIds.forEach(id => {
      this.operationService.deleteLocalProjectSpecificOperation(id);
    });
  }
}



// WEBPACK FOOTER //
// ./src/services/local/local-save.service.ts